import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import Topbar from "../Topbar";

const SOCIAL_LINKS = [
  { label: "Instagram", href: "https://www.instagram.com/pisu.tn/",       icon: "fab fa-instagram",  hoverColor: "hover:text-pink-400" },
  { label: "Facebook",  href: "https://www.facebook.com/61557530745252/", icon: "fab fa-facebook-f", hoverColor: "hover:text-blue-400" },
  { label: "TikTok",    href: "https://www.tiktok.com/@pisu.tn",          icon: "fab fa-tiktok",     hoverColor: "hover:text-white"    },
  { label: "YouTube",   href: "https://www.youtube.com/@Pisu-tn",         icon: "fab fa-youtube",    hoverColor: "hover:text-red-400"  },
];

const NAV_ITEMS = [
  { key: "home",        name: "Home",        href: "#home"        },
  { key: "bestsellers", name: "Best Sellers", href: "#best-sellers"},
  { key: "events",      name: "Events",       href: "#our-story"   },
  { key: "products",    name: "Products",     href: "#products"    },
  { key: "community",   name: "Community",    href: "#community"   },
  { key: "contact",     name: "Contact",      href: "#contact"     },
];

const Navbar = ({ topbarText }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = ""; };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <Topbar text={topbarText} />
      <nav className="max-w-7xl mx-auto flex justify-between items-center px-6 py-2">

        {/* Logo */}
        <a
          href="#home"
          className={`flex items-center transition-all duration-500 ${
            scrolled ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
          }`}
        >
          <img src="/assets/logo-black.png" alt="Pisu" className="h-14 w-auto" />
        </a>

        {/* Desktop nav links */}
        <ul className="hidden md:flex gap-8">
          {NAV_ITEMS.map((item) => (
            <li key={item.key}>
              <a
                href={item.href}
                className={`font-medium transition-colors duration-300 ${
                  scrolled
                    ? "text-background-dark hover:text-primary"
                    : "text-white hover:text-primary/90"
                }`}
              >
                {item.name}
              </a>
            </li>
          ))}
        </ul>

        {/* Desktop right side: social icons + lang toggle */}
        <div className="hidden md:flex items-center gap-4">
          {SOCIAL_LINKS.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              className={`text-lg transition-colors duration-200 ${
                scrolled ? `text-background-dark ${s.hoverColor}` : `text-white ${s.hoverColor}`
              }`}
            >
              <i className={s.icon} />
            </a>
          ))}
        </div>

        {/* Mobile right side: social icons + lang toggle + hamburger */}
        <div className="flex md:hidden items-center gap-1">
          <div className={`flex items-center gap-1 transition-all duration-300 ${scrolled ? "opacity-100" : "opacity-90"}`}>
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
                  scrolled
                    ? `bg-gray-100 text-background-dark ${s.hoverColor} hover:bg-gray-200`
                    : `bg-white/20 text-white backdrop-blur-sm ${s.hoverColor} hover:bg-white/30`
                }`}
              >
                <i className={`${s.icon} text-sm`} />
              </a>
            ))}
          </div>

          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            className={`flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 ml-1 ${
              scrolled
                ? "bg-gray-100 text-background-dark hover:bg-gray-200"
                : "bg-white/20 text-white backdrop-blur-sm hover:bg-white/30"
            }`}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-lg">
          <ul className="flex flex-col items-center py-6 gap-6">
            {NAV_ITEMS.map((item) => (
              <li key={item.key}>
                <a
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="block text-background-dark font-medium hover:text-primary transition-colors"
                >
                  {item.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
